import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter, Link, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';

import './App.css';
import { AdSenseLoader } from './AdSense';
import { api, authApi } from './api';
import ExamPage from './ExamPage';
import { AboutPage, ContactPage, PrivacyPolicyPage, TermsPage } from './LegalPages';
import NotFoundPage from './NotFoundPage';
import PremiumPage, { PremiumCancelPage, PremiumSuccessPage } from './PremiumPage';
import QuestionDemoPage from './QuestionDemoPage';
import { HomeSeoSections, SeoLandingPage, useSeo } from './SeoPages';
import StudyPage from './StudyPage';
import { useAuth } from './auth';
import { useI18n } from './i18n';
import { SupportedLanguage } from './QuestionPlayer';
import { getRuntimeConfigValue } from './runtimeConfig';

declare global {
  interface Window {
    google?: any;
  }
}

const MEDIA_BASE_URL = (getRuntimeConfigValue('REACT_APP_MEDIA_BASE_URL') || 'https://pub-2cdeeb37268f46c7b8d1013e3ab56448.r2.dev')
  .replace(/\/+$/, '');
const LANDING_HERO_URL = `${MEDIA_BASE_URL}/static/landing-hero.png`;

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
}

function formatDate(isoString: string, language: SupportedLanguage = 'pl'): string {
  const locales: Record<SupportedLanguage, string> = {
    pl: 'pl-PL',
    en: 'en-US',
    de: 'de-DE',
    ua: 'uk-UA',
  };
  return new Date(isoString).toLocaleDateString(locales[language], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

type GeneratedCode = {
  code: string;
  duration_days: number;
  created_at: string;
};

type CategoryOption = {
  id: string;
  name: string;
};

function extractErrorMessage(error: any, fallback: string) {
  const apiError = error?.response?.data;
  if (typeof apiError?.detail === 'string') {
    return apiError.detail;
  }
  if (apiError?.detail && typeof apiError.detail === 'object' && typeof apiError.detail.detail === 'string') {
    return apiError.detail.detail;
  }
  if (apiError && typeof apiError === 'object') {
    const firstValue = Object.values(apiError)[0];
    if (Array.isArray(firstValue) && typeof firstValue[0] === 'string') {
      return firstValue[0];
    }
    if (typeof firstValue === 'string') {
      return firstValue;
    }
  }
  return fallback;
}

function GoogleSignInButton({ onError }: { onError: (message: string) => void }) {
  const { loginWithGoogle } = useAuth();
  const { t } = useI18n();
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const clientId = getRuntimeConfigValue('REACT_APP_GOOGLE_CLIENT_ID');

  useEffect(() => {
    if (!clientId || !buttonRef.current) {
      return;
    }

    let cancelled = false;

    function renderGoogleButton() {
      if (cancelled || !window.google?.accounts?.id || !buttonRef.current) {
        return;
      }

      buttonRef.current.innerHTML = '';
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: { credential?: string }) => {
          if (!response.credential) {
            onError(t.home.googleFailed);
            return;
          }
          try {
            await loginWithGoogle(response.credential);
          } catch (error) {
            onError(extractErrorMessage(error, t.home.googleFailed));
          }
        },
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        width: 320,
        text: 'continue_with',
        shape: 'rectangular',
      });
    }

    if (window.google?.accounts?.id) {
      renderGoogleButton();
      return () => {
        cancelled = true;
      };
    }

    const existingScript = document.querySelector('script[data-google-identity="true"]') as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', renderGoogleButton, { once: true });
      return () => {
        cancelled = true;
      };
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset.googleIdentity = 'true';
    script.addEventListener('load', renderGoogleButton, { once: true });
    script.addEventListener('error', () => onError(t.home.googleLoadFailed), { once: true });
    document.head.appendChild(script);

    return () => {
      cancelled = true;
    };
  }, [clientId, loginWithGoogle, onError, t.home.googleFailed, t.home.googleLoadFailed]);

  if (!clientId) {
    return <p className="home-page__hint">{t.home.googleNotConfigured}</p>;
  }

  return <div ref={buttonRef} className="home-page__google-button" />;
}

function VerifyEmailPage() {
  const { verifyEmail } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState(t.verifyEmail.verifying);
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';

  useEffect(() => {
    async function runVerification() {
      if (!uid || !token) {
        setStatus('error');
        setMessage(t.verifyEmail.incomplete);
        return;
      }

      try {
        const detail = await verifyEmail(uid, token);
        setStatus('success');
        setMessage(detail);
      } catch (error) {
        setStatus('error');
        setMessage(extractErrorMessage(error, t.verifyEmail.failed));
      }
    }

    void runVerification();
  }, [t.verifyEmail.failed, t.verifyEmail.incomplete, token, uid, verifyEmail]);

  return (
    <div className="home-page">
      <section className="home-page__panel">
        <h1>{t.verifyEmail.title}</h1>
        <p className={status === 'error' ? 'home-page__error' : status === 'success' ? 'home-page__success' : ''}>
          {message}
        </p>
        <Link className="home-page__link" to="/">
          {t.verifyEmail.backToSignIn}
        </Link>
      </section>
    </div>
  );
}

function ResetPasswordPage() {
  const { confirmPasswordReset } = useAuth();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage('');
    setError('');
    setIsSubmitting(true);

    try {
      const detail = await confirmPasswordReset(uid, token, newPassword);
      setMessage(detail);
      setNewPassword('');
    } catch (submitError) {
      setError(extractErrorMessage(submitError, t.resetPassword.failed));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="home-page">
      <section className="home-page__panel">
        <h1>{t.resetPassword.title}</h1>
        {!uid || !token ? <p className="home-page__error">{t.resetPassword.incomplete}</p> : null}
        <form onSubmit={handleSubmit} className="home-page__grid">
          <input
            className="home-page__input"
            placeholder={t.home.newPassword}
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            required
            disabled={!uid || !token || isSubmitting}
          />
          {error ? <p className="home-page__error">{error}</p> : null}
          {message ? <p className="home-page__success">{message}</p> : null}
          <button className="home-page__button" type="submit" disabled={!uid || !token || isSubmitting}>
            {isSubmitting ? t.home.saving : t.resetPassword.setNewPassword}
          </button>
        </form>
        <Link className="home-page__link" to="/">
          {t.resetPassword.backToSignIn}
        </Link>
      </section>
    </div>
  );
}

function LandingPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useI18n();
  const copy = t.landing;

  useSeo({
    title: 'Szybkie Prawko - testy na prawo jazdy online i nauka do teorii',
    description:
      'Darmowe testy na prawo jazdy online, tryb nauki, przykładowe pytania i egzamin próbny dla kategorii A, B, C, D, T oraz pozostałych.',
    path: '/',
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Szybkie Prawko',
        url: 'https://szybkieprawko.online/',
        applicationCategory: 'EducationalApplication',
        operatingSystem: 'Web',
        inLanguage: 'pl-PL',
        description:
          'Aplikacja do nauki teorii na prawo jazdy: testy online, darmowy podgląd pytań i egzamin próbny.',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'PLN',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Czy mogę zacząć od darmowych testów na prawo jazdy?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Tak. Darmowy podgląd pytań pozwala sprawdzić sposób działania aplikacji i rozpocząć naukę bez płatności.',
            },
          },
          {
            '@type': 'Question',
            name: 'Czy testy na prawo jazdy online pomagają przygotować się do egzaminu?',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'Tak. Testy online pomagają ćwiczyć pytania egzaminacyjne, tempo odpowiedzi i analizę błędów przed egzaminem teoretycznym.',
            },
          },
        ],
      },
    ],
  });

  return (
    <main className="landing-page">
      <section className="landing-hero">
        <div className="landing-hero__content">
          <span className="landing-hero__eyebrow">{copy.eyebrow}</span>
          <h1>{copy.title}</h1>
          <p>{copy.description}</p>
          <div className="landing-hero__actions">
            <Link className="landing-button landing-button--primary" to={isAuthenticated ? '/study' : '/login'}>
              {copy.primaryCta}
            </Link>
            <Link className="landing-button landing-button--secondary" to="/exam">
              {copy.secondaryCta}
            </Link>
          </div>
          <div className="landing-hero__metrics">
            <div><strong>3700+</strong><span>{copy.metricQuestions}</span></div>
            <div><strong>{copy.metricPriceValue}</strong><span>{copy.metricPrice}</span></div>
            <div><strong>{copy.metricCategoriesValue}</strong><span>{copy.metricCategories}</span></div>
          </div>
        </div>
        <div className="landing-hero__visual">
          <img src={LANDING_HERO_URL} alt={copy.heroAlt} loading="eager" decoding="async" />
        </div>
      </section>

      <section className="landing-section landing-section--features">
        <article>
          <span>01</span>
          <h2>{copy.featureOneTitle}</h2>
          <p>{copy.featureOneText}</p>
        </article>
        <article>
          <span>02</span>
          <h2>{copy.featureTwoTitle}</h2>
          <p>{copy.featureTwoText}</p>
        </article>
        <article>
          <span>03</span>
          <h2>{copy.featureThreeTitle}</h2>
          <p>{copy.featureThreeText}</p>
        </article>
      </section>

      <section className="landing-proof">
        <div>
          <h2>{copy.proofTitle}</h2>
          <p>{copy.proofText}</p>
        </div>
        <Link className="landing-button landing-button--primary" to={isAuthenticated ? '/profile' : '/login'}>
          {isAuthenticated ? copy.profileCta : copy.createAccountCta}
        </Link>
      </section>

      <HomeSeoSections />
    </main>
  );
}

function LoginPage() {
  const { isAuthenticated, login, register, resendVerification, requestPasswordReset } = useAuth();
  const { t } = useI18n();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setMessage('');
    setShowResendVerification(false);
    setIsSubmitting(true);

    try {
      if (mode === 'login') {
        await login(identifier, password);
      } else {
        const result = await register(username, email, password);
        setMessage(result.detail);
        setMode('login');
        setIdentifier(email);
      }
      setPassword('');
    } catch (submitError: any) {
      const apiError = submitError?.response?.data;
      const isUnverified =
        apiError?.detail?.code === 'email_not_verified' ||
        apiError?.code === 'email_not_verified' ||
        extractErrorMessage(submitError, '').toLowerCase().includes('verify your email');
      setShowResendVerification(isUnverified);
      setError(extractErrorMessage(submitError, t.home.authenticationFailed));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    setMessage('');
    setIsSendingReset(true);

    try {
      const detail = await requestPasswordReset(resetEmail || identifier);
      setMessage(detail);
    } catch (requestError) {
      setError(extractErrorMessage(requestError, t.home.authenticationFailed));
    } finally {
      setIsSendingReset(false);
    }
  }

  async function handleResendVerification() {
    setError('');
    setMessage('');
    setIsResendingVerification(true);

    try {
      const detail = await resendVerification(identifier || email);
      setMessage(detail);
      setShowResendVerification(false);
    } catch (requestError) {
      setError(extractErrorMessage(requestError, t.home.authenticationFailed));
    } finally {
      setIsResendingVerification(false);
    }
  }

  if (isAuthenticated) {
    return (
      <main className="auth-page">
        <section className="auth-card">
          <h1>{t.home.alreadySignedInTitle}</h1>
          <p>{t.home.alreadySignedInText}</p>
          <div className="auth-card__actions">
            <Link className="landing-button landing-button--primary" to="/study">{t.home.studyModeCta}</Link>
            <Link className="landing-button landing-button--secondary" to="/profile">{t.home.profileCta}</Link>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-page">
      <section className="auth-card">
        <Link className="auth-card__back" to="/">← {t.home.backToHome}</Link>
        <h1>{mode === 'login' ? t.home.signInTitle : t.home.createAccountTitle}</h1>
        <p>{t.home.authIntro}</p>

        <div className="auth-card__tabs">
          <button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>{t.home.loginTab}</button>
          <button type="button" className={mode === 'register' ? 'active' : ''} onClick={() => setMode('register')}>{t.home.registerTab}</button>
        </div>

        <form onSubmit={handleSubmit} className="auth-card__form">
          {mode === 'register' ? (
            <>
              <input placeholder={t.home.usernamePlaceholder} value={username} onChange={(event) => setUsername(event.target.value)} required />
              <input placeholder={t.home.emailPlaceholder} type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </>
          ) : (
            <input placeholder={t.home.emailOrUsername} value={identifier} onChange={(event) => setIdentifier(event.target.value)} required />
          )}
          <input placeholder={t.home.password} type="password" value={password} onChange={(event) => setPassword(event.target.value)} required />

          {error ? (
            <p className="auth-card__error">
              {error}
              {showResendVerification ? (
                <>
                  {' '}
                  <button type="button" onClick={handleResendVerification} disabled={isResendingVerification}>
                    {isResendingVerification ? t.home.sending : t.home.resendVerificationLink}
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
          {message ? <p className="auth-card__success">{message}</p> : null}

          <button className="auth-card__submit" type="submit" disabled={isSubmitting}>
            {isSubmitting ? t.home.pleaseWait : mode === 'login' ? t.home.login : t.home.createAccount}
          </button>
        </form>

        {mode === 'login' ? (
          <button type="button" className="auth-card__link-button" onClick={() => setShowResetPassword((value) => !value)}>
            {t.home.forgotPassword}
          </button>
        ) : null}

        {showResetPassword ? (
          <form onSubmit={handlePasswordReset} className="auth-card__reset">
            <input
              type="email"
              placeholder={t.home.accountEmail}
              value={resetEmail}
              onChange={(event) => setResetEmail(event.target.value)}
              required
            />
            <button type="submit" disabled={isSendingReset}>
              {isSendingReset ? t.home.sending : t.home.sendResetEmail}
            </button>
          </form>
        ) : null}

        <div className="auth-card__divider">{t.home.or}</div>
        <GoogleSignInButton onError={setError} />
      </section>
    </main>
  );
}

function AccountPage() {
  const {
    isAuthenticated,
    isLoading,
    login,
    logout,
    register,
    refreshUser,
    user,
    resendVerification,
    requestPasswordReset,
    changePassword,
    updatePreferences,
    deleteAccount,
  } = useAuth();
  const { language, t } = useI18n();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [identifier, setIdentifier] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [premiumCode, setPremiumCode] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeemError, setRedeemError] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [durationDays, setDurationDays] = useState('30');
  const [quantity, setQuantity] = useState('1');
  const [generatedCodes, setGeneratedCodes] = useState<GeneratedCode[]>([]);
  const [adminError, setAdminError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [preferredExamCategory, setPreferredExamCategory] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState<SupportedLanguage>('pl');
  const [preferencesMessage, setPreferencesMessage] = useState('');
  const [preferencesError, setPreferencesError] = useState('');
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const resendEmailValue = useMemo(() => resendEmail || email || identifier, [email, identifier, resendEmail]);

  useEffect(() => {
    if (!isAuthenticated) {
      setCategories([]);
      setPreferredExamCategory('');
      return;
    }

    async function loadCategories() {
      try {
        const response = await api.get<CategoryOption[]>('/categories/');
        setCategories(response.data);
      } catch (error) {
        setCategories([]);
      }
    }

    void loadCategories();
  }, [isAuthenticated]);

  useEffect(() => {
    setPreferredExamCategory(user?.preferred_exam_category || '');
    setPreferredLanguage(user?.preferred_language || 'pl');
  }, [user?.preferred_exam_category, user?.preferred_language]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAuthError('');
    setAuthMessage('');
    setIsSubmitting(true);

    try {
      if (mode === 'login') {
        await login(identifier, password);
      } else {
        const result = await register(username, email, password);
        setAuthMessage(result.detail);
        setResendEmail(email);
        setResetEmail(email);
        setMode('login');
        setIdentifier(email);
      }
      setPassword('');
    } catch (submitError) {
      setAuthError(extractErrorMessage(submitError, t.home.authenticationFailed));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleRedeemCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRedeemError('');
    setRedeemMessage('');
    setIsRedeeming(true);

    try {
      const response = await authApi.post('/premium/redeem/', { code: premiumCode });
      await refreshUser();
      setPremiumCode('');
      setRedeemMessage(`${t.home.premiumActiveUntil} ${response.data.premium_until}.`);
    } catch (redeemRequestError) {
      setRedeemError(extractErrorMessage(redeemRequestError, t.home.redeemFailed));
    } finally {
      setIsRedeeming(false);
    }
  }

  async function handleGenerateCodes(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAdminError('');
    setIsGenerating(true);

    try {
      const response = await authApi.post('/premium-codes/', {
        duration_days: Number(durationDays),
        quantity: Number(quantity),
      });
      setGeneratedCodes(response.data.codes);
    } catch (generateError) {
      setAdminError(extractErrorMessage(generateError, t.home.generateFailed));
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleRequestReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setResetError('');
    setResetMessage('');
    setIsSendingReset(true);

    try {
      const detail = await requestPasswordReset(resetEmail);
      setResetMessage(detail);
    } catch (requestError) {
      setResetError(extractErrorMessage(requestError, t.home.authenticationFailed));
    } finally {
      setIsSendingReset(false);
    }
  }

  async function handleResendVerification(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setResendError('');
    setResendMessage('');
    setIsResending(true);

    try {
      const detail = await resendVerification(resendEmailValue);
      setResendMessage(detail);
    } catch (requestError) {
      setResendError(extractErrorMessage(requestError, t.home.authenticationFailed));
    } finally {
      setIsResending(false);
    }
  }

  async function handleChangePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPasswordError('');
    setPasswordMessage('');
    setIsChangingPassword(true);

    try {
      const detail = await changePassword(currentPassword, newPassword);
      setPasswordMessage(detail);
      setCurrentPassword('');
      setNewPassword('');
    } catch (submitError) {
      setPasswordError(extractErrorMessage(submitError, t.home.changePasswordFailed));
    } finally {
      setIsChangingPassword(false);
    }
  }

  async function handleSavePreferences(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!preferredExamCategory) {
      return;
    }

    setPreferencesError('');
    setPreferencesMessage('');
    setIsSavingPreferences(true);

    try {
      await updatePreferences({ preferredExamCategory, preferredLanguage });
      setPreferencesMessage(t.home.preferencesSaved);
    } catch (submitError) {
      setPreferencesError(extractErrorMessage(submitError, t.home.preferencesFailed));
    } finally {
      setIsSavingPreferences(false);
    }
  }

  async function handleDeleteAccount(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setDeleteError('');
    setIsDeleting(true);

    try {
      await deleteAccount(deleteConfirmation, deletePassword);
      setDeleteConfirmation('');
      setDeletePassword('');
    } catch (submitError) {
      setDeleteError(extractErrorMessage(submitError, t.home.deleteAccountFailed));
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <div className="home-page">
      <div className="home-page__headline">
        <h1>{t.home.title}</h1>
        {user?.account_tier === 'PREMIUM' ? <span className="home-page__badge home-page__badge--premium">Premium</span> : null}
        {user?.account_tier === 'ADMIN' ? <span className="home-page__badge home-page__badge--admin">Admin</span> : null}
      </div>

      {isLoading ? (
        <p>{t.common.loadingSession}</p>
      ) : isAuthenticated && user ? (
        <div className="home-page__account-layout--centered">
          <div className="home-page__account-content">
            <section className="home-page__panel" id="account-status">
              <div className="account-card">
                <div className="account-card__avatar">
                  {user.username.slice(0, 2).toUpperCase()}
                </div>
                <div className="account-card__info">
                  <span className="account-card__username">{user.username}</span>
                  <span className="account-card__email">{user.email || t.home.noEmail}</span>
                </div>
                {user.account_tier === 'ADMIN' && (
                  <span className="home-page__badge home-page__badge--admin">Admin</span>
                )}
                {user.account_tier === 'PREMIUM' && (
                  <span className="home-page__badge home-page__badge--premium">Premium</span>
                )}
              </div>

              {user.is_premium && user.premium_until ? (
                <p className="account-card__premium-until">
                  {t.home.premiumActive} <strong>{formatDate(user.premium_until, language)}</strong>
                </p>
              ) : null}

              <div className="account-card__meta">
                <span className={`account-card__meta-item ${user.is_email_verified ? 'account-card__meta-item--ok' : 'account-card__meta-item--warn'}`}>
                  {user.is_email_verified ? '✓' : '!'} {user.is_email_verified ? t.home.emailVerifiedShort : t.home.emailUnverifiedShort}
                </span>
                <span className="account-card__meta-item">
                  {t.home.loginMethod}: {user.providers.join(', ')}
                </span>
              </div>

              <div className="home-page__actions" style={{ marginTop: 16 }}>
                <button className="home-page__button" onClick={() => void logout()}>
                  {t.home.logout}
                </button>
              </div>
            </section>

            {user.has_local_password ? (
              <section className="home-page__panel" id="account-settings">
                <h2>{t.home.changePasswordTitle}</h2>
                <form onSubmit={handleChangePassword} className="home-page__grid">
                  <input
                    className="home-page__input"
                    type="password"
                    placeholder={t.home.currentPassword}
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    required
                  />
                  <input
                    className="home-page__input"
                    type="password"
                    placeholder={t.home.newPassword}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    required
                  />
                  {passwordError ? <p className="home-page__error">{passwordError}</p> : null}
                  {passwordMessage ? <p className="home-page__success">{passwordMessage}</p> : null}
                  <button className="home-page__button" type="submit" disabled={isChangingPassword}>
                    {isChangingPassword ? t.home.saving : t.home.changePassword}
                  </button>
                </form>
              </section>
            ) : null}

            <section className="home-page__panel">
              <h2>{t.home.preferencesTitle}</h2>
              <form onSubmit={handleSavePreferences} className="home-page__grid">
                <select
                  className="home-page__select"
                  value={preferredExamCategory}
                  onChange={(event) => setPreferredExamCategory(event.target.value)}
                  required
                >
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <select
                  className="home-page__select"
                  value={preferredLanguage}
                  onChange={(event) => setPreferredLanguage(event.target.value as SupportedLanguage)}
                  required
                >
                  {t.languages.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {preferencesError ? <p className="home-page__error">{preferencesError}</p> : null}
                {preferencesMessage ? <p className="home-page__success">{preferencesMessage}</p> : null}
                <button className="home-page__button" type="submit" disabled={isSavingPreferences || !preferredExamCategory}>
                  {isSavingPreferences ? t.home.saving : t.home.savePreferences}
                </button>
              </form>
            </section>

            <section className="home-page__panel" id={user.has_local_password ? undefined : 'account-settings'}>
              <h2>{t.home.deleteAccountTitle}</h2>
              <p className="home-page__hint">{t.home.deleteAccountHint}</p>
              <form onSubmit={handleDeleteAccount} className="home-page__grid">
                <input
                  className="home-page__input"
                  placeholder={t.home.usernameConfirmation}
                  value={deleteConfirmation}
                  onChange={(event) => setDeleteConfirmation(event.target.value)}
                  required
                />
                {user.has_local_password ? (
                  <input
                    className="home-page__input"
                    type="password"
                    placeholder={t.home.currentPassword}
                    value={deletePassword}
                    onChange={(event) => setDeletePassword(event.target.value)}
                    required
                  />
                ) : null}
                {deleteError ? <p className="home-page__error">{deleteError}</p> : null}
                <button className="home-page__button home-page__button--danger" type="submit" disabled={isDeleting}>
                  {isDeleting ? t.home.deleting : t.home.deleteAccount}
                </button>
              </form>
            </section>

            <section className="home-page__panel" id="premium-status">
              <h2>{t.home.activatePremiumCode}</h2>
              <form onSubmit={handleRedeemCode} className="home-page__grid">
                <input
                  className="home-page__input"
                  placeholder={t.home.premiumCodePlaceholder}
                  value={premiumCode}
                  onChange={(event) => setPremiumCode(event.target.value)}
                  required
                />
                {redeemError ? <p className="home-page__error">{redeemError}</p> : null}
                {redeemMessage ? <p className="home-page__success">{redeemMessage}</p> : null}
                <button className="home-page__button" type="submit" disabled={isRedeeming}>
                  {isRedeeming ? t.home.redeeming : t.home.redeemCode}
                </button>
              </form>
            </section>

            {user.is_admin ? (
              <section className="home-page__panel">
                <h2>{t.home.generatePremiumCodes}</h2>
                <form onSubmit={handleGenerateCodes} className="home-page__grid home-page__grid--two-columns">
                  <select className="home-page__select" value={durationDays} onChange={(event) => setDurationDays(event.target.value)}>
                    <option value="7">7 {t.common.days}</option>
                    <option value="30">30 {t.common.days}</option>
                    <option value="180">180 {t.common.days}</option>
                  </select>
                  <input
                    className="home-page__input"
                    type="number"
                    min="1"
                    max="100"
                    value={quantity}
                    onChange={(event) => setQuantity(event.target.value)}
                  />
                  {adminError ? <p className="home-page__error home-page__full-row">{adminError}</p> : null}
                  <button className="home-page__button home-page__full-row" type="submit" disabled={isGenerating}>
                    {isGenerating ? t.home.generating : t.home.generateCodes}
                  </button>
                </form>

                {generatedCodes.length > 0 ? (
                  <div className="home-page__code-list">
                    {generatedCodes.map((generatedCode) => (
                      <div key={generatedCode.code} className="home-page__code-item">
                        <span>{generatedCode.code}</span>
                        <span>{generatedCode.duration_days} {t.common.days}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}
          </div>

        </div>
      ) : (
        <section className="home-page__panel home-page__profile-login">
          <h1>{t.home.profileLoginTitle}</h1>
          <p className="home-page__lead">{t.home.profileLoginText}</p>
          <Link className="home-page__button" to="/login">{t.home.login}</Link>
        </section>
      )}
    </div>
  );
}

function UserPanel() {
  const { isAuthenticated, logout, updatePreferences, user } = useAuth();
  const { language, setLanguage, t } = useI18n();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const tierLabel =
    user?.account_tier === 'ADMIN'
      ? t.userMenu.tierAdmin
      : user?.account_tier === 'PREMIUM'
        ? t.userMenu.tierPremium
        : t.userMenu.tierFree;

  return (
    <div className="user-panel">
      <label className="user-panel__language">
        <span className="user-panel__language-label">{t.exam.languageLabel}</span>
        <select
          className="user-panel__select"
          value={language}
          onChange={(event) => {
            const nextLanguage = event.target.value as SupportedLanguage;
            setLanguage(nextLanguage);
            if (isAuthenticated) {
              void updatePreferences({ preferredLanguage: nextLanguage });
            }
          }}
        >
          {t.languages.map((option) => (
            <option key={option.code} value={option.code}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div
        className="user-panel__menu"
        onMouseEnter={() => setIsMenuOpen(true)}
        onMouseLeave={() => setIsMenuOpen(false)}
      >
        <button
          className="user-panel__summary"
          type="button"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen((current) => !current)}
        >
          <span className="user-panel__username">{isAuthenticated && user ? user.username : t.userMenu.anonymous}</span>
          <span className={`user-panel__status user-panel__status--${user?.account_tier?.toLowerCase() || 'free'}`}>
            {isAuthenticated && user ? tierLabel : '-'}
          </span>
        </button>
        {isMenuOpen ? (
          <div className="user-panel__dropdown">
            {isAuthenticated ? (
              <>
                <Link className="user-panel__item" to="/profile#account-settings" onClick={() => setIsMenuOpen(false)}>
                  {t.userMenu.settings}
                </Link>
                <Link className="user-panel__item" to="/profile#account-status" onClick={() => setIsMenuOpen(false)}>
                  {t.userMenu.status}
                </Link>
                <button
                  className="user-panel__item user-panel__item--button"
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    void logout();
                  }}
                >
                  {t.userMenu.logout}
                </button>
              </>
            ) : (
              <Link className="user-panel__item" to="/login" onClick={() => setIsMenuOpen(false)}>
                {t.home.login}
              </Link>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function App() {
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();

  return (
    <BrowserRouter>
      <ScrollToTop />
      <AdSenseLoader />
      <div className="app-shell">
        <nav className="app-nav">
          <Link to="/" className="app-nav__brand">
            <span className="app-nav__logo">SP</span>
            <span className="app-nav__brand-text">Prawo Jazdy</span>
          </Link>
          <div className="app-nav__links">
            <Link to="/study">{t.nav.study}</Link>
            <Link to="/testy-na-prawo-jazdy">Testy online</Link>
            {!isAuthenticated ? <Link to="/question-demo">{t.nav.questionDemo}</Link> : null}
            <Link to="/exam">{t.nav.exam}</Link>
            <Link to="/premium" className="app-nav__premium">Premium</Link>
          </div>
          <UserPanel />
        </nav>
        <main className="app-main">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/profile" element={<AccountPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/study" element={<StudyPage />} />
            <Route path="/question-demo" element={<QuestionDemoPage />} />
            <Route path="/exam" element={<ExamPage />} />
            <Route path="/egzamin-na-prawo-jazdy-kategoria-b" element={<SeoLandingPage slug="egzamin-na-prawo-jazdy-kategoria-b" />} />
            <Route path="/testy-na-prawo-jazdy" element={<SeoLandingPage slug="testy-na-prawo-jazdy" />} />
            <Route path="/darmowe-testy-na-prawo-jazdy" element={<SeoLandingPage slug="darmowe-testy-na-prawo-jazdy" />} />
            <Route path="/testy-na-prawo-jazdy-online" element={<SeoLandingPage slug="testy-na-prawo-jazdy-online" />} />
            <Route path="/nauka-na-prawo-jazdy" element={<SeoLandingPage slug="nauka-na-prawo-jazdy" />} />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/premium/success" element={<PremiumSuccessPage />} />
            <Route path="/premium/cancel" element={<PremiumCancelPage />} />
            <Route path="/o-nas" element={<AboutPage />} />
            <Route path="/kontakt" element={<ContactPage />} />
            <Route path="/polityka-prywatnosci" element={<PrivacyPolicyPage />} />
            <Route path="/regulamin" element={<TermsPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </main>
        <footer className="app-footer">
          <span>© 2026 Szybkie Prawko</span>
          <Link to="/testy-na-prawo-jazdy">Testy na prawo jazdy</Link>
          <Link to="/egzamin-na-prawo-jazdy-kategoria-b">Egzamin kat. B</Link>
          <Link to="/darmowe-testy-na-prawo-jazdy">Darmowe testy</Link>
          <Link to="/testy-na-prawo-jazdy-online">Testy online</Link>
          <Link to="/nauka-na-prawo-jazdy">Nauka</Link>
          <Link to="/o-nas">O nas</Link>
          <Link to="/kontakt">Kontakt</Link>
          <Link to="/polityka-prywatnosci">Polityka prywatności</Link>
          <Link to="/regulamin">Regulamin</Link>
        </footer>
      </div>
    </BrowserRouter>
  );
}

export default App;
